'use client';

import { c as _c } from "react/compiler-runtime";
import { type DirectPurchaseCheckout, directPurchaseFiat, type FundingOperation, formatFiatUnits, isFundingHostPicker, type OnrampClient, onrampManifest } from '@morfi/onramp-sdk';
import { useFundingAmountDraft, useFundingCurrency, useFundingInlineCheckout } from '@morfi/onramp-sdk/react';
import type { OnrampRealtime } from '@morfi/onramp-sdk/realtime';
import { createMoonPayWebProvider, type MoonPayWebProvider, webFundingMethods } from '@morfi/onramp-sdk/web';
import { useLocale, useTranslations } from 'next-intl';
import { type ReactNode, useEffect, useEffectEvent } from 'react';
import { TOAST_IDS } from '@/config/toast-ids';
import { useFeatureGate } from '@/hooks/use-feature-gate';
import { getActiveNetwork } from '@/lib/network/active';
import { logger } from '@/lib/telemetry/logger';
import { showToast } from '@/lib/ui/toast';
import { useExchangeRates } from '@/providers/exchange-rate-provider';
import { useCurrencyStore } from '@/stores/currency';
import { useFundingLocationOverride } from '../devtools/use-funding-location-override';
import { fundingAmountStorage } from '../shared/funding-amount-storage';
import { useFundingStage } from '../shared/funding-chrome';
import { fundingLimitStorage } from '../shared/funding-limit-storage';
import { useFundingPageActive } from '../shared/use-funding-page-active';
import { useFundingResource } from '../shared/use-funding-resource';
import { checkoutOwnsChrome } from './funding-checkout-chrome';
import { FundingCheckoutView } from './funding-checkout-view';
import { FundingMethodPicker } from './funding-method-picker';
export interface Props {
  directPurchase?: DirectPurchaseCheckout;
  hostActive?: boolean;
  checkoutRevision?: number;
  open?: boolean;
  render?(content: ReactNode): ReactNode;
  realtime?: OnrampRealtime;
  client: OnrampClient;
  sessionToken: string;
  sessionProofId: string;
  authenticationRequested?: boolean;
  authenticationBusy?: boolean;
  authenticationError?: boolean;
  onAcceptAuthenticationTerms?(version: string): Promise<void>;
  onAuthenticateSandbox?(email: string): Promise<void>;
  onSessionRefresh(reason?: 'disconnect'): void;
  onClose(): void;
  onSignIn(): void;
  onExchange?: (() => void) | undefined;
}
export function FundingCheckout(props) {
  const $ = _c(5);
  let t0;
  if ($[0] !== props.sessionToken) {
    t0 = () => createMoonPayWebProvider({
      sessionToken: props.sessionToken,
      theme: {
        appearance: "dark"
      }
    });
    $[0] = props.sessionToken;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const provider = useFundingResource(t0);
  let t1;
  if ($[2] !== props || $[3] !== provider) {
    t1 = provider ? <CheckoutBody key={props.checkoutRevision} {...props} provider={provider} /> : props.render ? props.render(<FundingMethodPicker client={props.client} methods={[]} onExchange={props.onExchange} loading={true} />) : <FundingMethodPicker client={props.client} methods={[]} onExchange={props.onExchange} loading={true} />;
    $[2] = props;
    $[3] = provider;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
}
function CheckoutBody(t0) {
  const $ = _c(107);
  const {
    client,
    realtime,
    sessionProofId,
    directPurchase,
    authenticationRequested: t1,
    authenticationBusy: t2,
    authenticationError: t3,
    onAcceptAuthenticationTerms,
    onAuthenticateSandbox,
    provider,
    onSessionRefresh,
    onSignIn,
    onExchange,
    render,
    open: t4,
    onClose,
    hostActive: t5
  } = t0;
  const authenticationRequested = t1 === undefined ? false : t1;
  const authenticationBusy = t2 === undefined ? false : t2;
  const authenticationError = t3 === undefined ? false : t3;
  const open = t4 === undefined ? true : t4;
  const hostActive = t5 === undefined ? true : t5;
  const t = useTranslations("onramp");
  const locale = useLocale();
  const onrampEnabled = useFeatureGate("onramp_enabled");
  const directPurchaseEnabled = useFeatureGate("direct_token_purchase_enabled");
  const selectedCurrency = useCurrencyStore(_temp);
  const rates = useExchangeRates();
  const {
    currency,
    decimals,
    fx
  } = useFundingCurrency(selectedCurrency, rates.fundingFx);
  const pageActive = useFundingPageActive();
  const sandboxIgnoreLocationRestrictions = useFundingLocationOverride();
  const t6 = onrampEnabled === true && directPurchaseEnabled === true;
  const t7 = hostActive && open && pageActive && !authenticationBusy;
  const t8 = directPurchase ? directPurchaseFiat.currency : currency;
  let t9;
  if ($[0] !== directPurchase || $[1] !== fx) {
    t9 = directPurchase ? {
      directPurchase
    } : {
      fundingFx: fx
    };
    $[0] = directPurchase;
    $[1] = fx;
    $[2] = t9;
  } else {
    t9 = $[2];
  }
  const t10 = rates.isLoading;
  const t11 = directPurchase ? directPurchaseFiat.decimals : decimals;
  let t12;
  if ($[3] !== t) {
    t12 = status => {
      showToast({
        id: TOAST_IDS.ONRAMP_PAYMENT_STATUS,
        message: t(`status.${status}`),
        variant: "info"
      });
    };
    $[3] = t;
    $[4] = t12;
  } else {
    t12 = $[4];
  }
  let t13;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = crypto.randomUUID.bind(crypto);
    $[5] = t13;
  } else {
    t13 = $[5];
  }
  let t14;
  if ($[6] !== authenticationRequested || $[7] !== client || $[8] !== locale || $[9] !== onSessionRefresh || $[10] !== provider || $[11] !== rates.isLoading || $[12] !== sandboxIgnoreLocationRestrictions || $[13] !== sessionProofId || $[14] !== t11 || $[15] !== t12 || $[16] !== t6 || $[17] !== t7 || $[18] !== t8 || $[19] !== t9) {
    t14 = {
      sandboxIgnoreLocationRestrictions,
      client,
      directPurchaseEnabled: t6,
      manifest: onrampManifest,
      sessionProofId,
      limitStorage: fundingLimitStorage,
      authenticationRequested,
      provider,
      surface: "web",
      active: t7,
      currency: t8,
      ...t9,
      fundingFxLoading: t10,
      locale,
      decimals: t11,
      deviceMethods: webFundingMethods,
      onPaymentStatus: t12,
      onConnectionCheck: _temp2,
      onSessionRefresh,
      createId: t13
    };
    $[6] = authenticationRequested;
    $[7] = client;
    $[8] = locale;
    $[9] = onSessionRefresh;
    $[10] = provider;
    $[11] = rates.isLoading;
    $[12] = sandboxIgnoreLocationRestrictions;
    $[13] = sessionProofId;
    $[14] = t11;
    $[15] = t12;
    $[16] = t6;
    $[17] = t7;
    $[18] = t8;
    $[19] = t9;
    $[20] = t14;
  } else {
    t14 = $[20];
  }
  const checkout = useFundingInlineCheckout(t14);
  const t15 = checkout.state === "methods" ? null : checkout.method;
  let t16;
  if ($[21] !== client || $[22] !== directPurchase) {
    t16 = !directPurchase && client.progressKey ? {
      persistence: {
        scope: `${client.progressKey}:${getActiveNetwork()}`,
        direction: "onramp" as const,
        storage: fundingAmountStorage
      }
    } : {};
    $[21] = client;
    $[22] = directPurchase;
    $[23] = t16;
  } else {
    t16 = $[23];
  }
  let t17;
  if ($[24] !== checkout.amountUnits || $[25] !== checkout.currency || $[26] !== checkout.decimals || $[27] !== locale || $[28] !== t15 || $[29] !== t16) {
    t17 = {
      method: t15,
      currency: checkout.currency,
      decimals: checkout.decimals,
      locale,
      amountUnits: checkout.amountUnits,
      ...t16
    };
    $[24] = checkout.amountUnits;
    $[25] = checkout.currency;
    $[26] = checkout.decimals;
    $[27] = locale;
    $[28] = t15;
    $[29] = t16;
    $[30] = t17;
  } else {
    t17 = $[30];
  }
  const amountDraft = useFundingAmountDraft(t17);
  const restoredUnits = amountDraft.units;
  let t18;
  if ($[31] !== checkout || $[32] !== restoredUnits) {
    t18 = () => {
      if (checkout.state === "amount" && checkout.amountUnits === null && restoredUnits !== null) {
        checkout.previewAmount(restoredUnits);
      }
    };
    $[31] = checkout;
    $[32] = restoredUnits;
    $[33] = t18;
  } else {
    t18 = $[33];
  }
  let t19;
  if ($[34] !== checkout.amountUnits || $[35] !== checkout.previewAmount || $[36] !== checkout.state || $[37] !== restoredUnits) {
    t19 = [checkout.state, checkout.amountUnits, checkout.previewAmount, restoredUnits];
    $[34] = checkout.amountUnits;
    $[35] = checkout.previewAmount;
    $[36] = checkout.state;
    $[37] = restoredUnits;
    $[38] = t19;
  } else {
    t19 = $[38];
  }
  useEffect(t18, t19);
  const boundaryUnits = checkout.amountBoundary?.amountUnits;
  const boundaryKind = checkout.amountBoundary?.kind;
  const enteredUnits = checkout.amountUnits;
  let t20;
  if ($[39] !== checkout.currency || $[40] !== checkout.decimals || $[41] !== locale || $[42] !== t) {
    t20 = (units, kind) => {
      const amount = formatFiatUnits(units, checkout.decimals, checkout.currency, locale);
      showToast({
        id: TOAST_IDS.ONRAMP_AMOUNT_ERROR,
        message: kind === "minimum" ? t("amount_minimum", {
          amount
        }) : t("amount_maximum", {
          amount
        }),
        variant: "error"
      });
    };
    $[39] = checkout.currency;
    $[40] = checkout.decimals;
    $[41] = locale;
    $[42] = t;
    $[43] = t20;
  } else {
    t20 = $[43];
  }
  const notifyAmountBoundary = useEffectEvent(t20);
  let t21;
  if ($[44] !== boundaryKind || $[45] !== boundaryUnits || $[46] !== checkout.state || $[47] !== enteredUnits || $[48] !== notifyAmountBoundary) {
    t21 = () => {
      if (!boundaryUnits || !boundaryKind || !enteredUnits || checkout.state !== "amount") {
        return;
      }
      const timer = setTimeout(() => {
        notifyAmountBoundary(boundaryUnits, boundaryKind);
      }, 800);
      return () => clearTimeout(timer);
    };
    $[44] = boundaryKind;
    $[45] = boundaryUnits;
    $[46] = checkout.state;
    $[47] = enteredUnits;
    $[48] = notifyAmountBoundary;
    $[49] = t21;
  } else {
    t21 = $[49];
  }
  let t22;
  if ($[50] !== boundaryKind || $[51] !== boundaryUnits || $[52] !== checkout.state || $[53] !== enteredUnits) {
    t22 = [boundaryUnits, boundaryKind, enteredUnits, checkout.state];
    $[50] = boundaryKind;
    $[51] = boundaryUnits;
    $[52] = checkout.state;
    $[53] = enteredUnits;
    $[54] = t22;
  } else {
    t22 = $[54];
  }
  useEffect(t21, t22);
  const t23 = directPurchase ? "direct_purchase_title" : "deposit_title";
  let t24;
  if ($[55] !== t || $[56] !== t23) {
    t24 = t(t23);
    $[55] = t;
    $[56] = t23;
    $[57] = t24;
  } else {
    t24 = $[57];
  }
  let t25;
  if ($[58] !== authenticationRequested || $[59] !== checkout || $[60] !== directPurchase || $[61] !== hostActive) {
    t25 = hostActive && !isFundingHostPicker(checkout, Boolean(directPurchase), authenticationRequested) && checkoutOwnsChrome(checkout.state, checkout.customerVerification);
    $[58] = authenticationRequested;
    $[59] = checkout;
    $[60] = directPurchase;
    $[61] = hostActive;
    $[62] = t25;
  } else {
    t25 = $[62];
  }
  useFundingStage(t24, checkout.state === "methods", checkout.hasBack ? checkout.back : undefined, t25, checkout.state === "methods" ? checkout.openProfile : undefined);
  let t26;
  if ($[63] !== checkout.error || $[64] !== checkout.method || $[65] !== checkout.preview.error || $[66] !== checkout.preview.providerFailure?.category || $[67] !== checkout.preview.providerFailure?.code || $[68] !== checkout.preview.providerFailure?.sourceBounds || $[69] !== checkout.providerFailure?.code || $[70] !== checkout.state) {
    t26 = () => {
      const code = checkout.error ?? checkout.preview.error;
      if (code && !(checkout.state === "amount" && !checkout.error && checkout.preview.providerFailure?.category === "connection_required")) {
        logger.warn("onramp.checkout_failed", {
          phase: checkout.state,
          method: checkout.method,
          code,
          providerCode: checkout.providerFailure?.code ?? checkout.preview.providerFailure?.code,
          hasSourceBounds: Boolean(checkout.preview.providerFailure?.sourceBounds)
        });
      }
    };
    $[63] = checkout.error;
    $[64] = checkout.method;
    $[65] = checkout.preview.error;
    $[66] = checkout.preview.providerFailure?.category;
    $[67] = checkout.preview.providerFailure?.code;
    $[68] = checkout.preview.providerFailure?.sourceBounds;
    $[69] = checkout.providerFailure?.code;
    $[70] = checkout.state;
    $[71] = t26;
  } else {
    t26 = $[71];
  }
  const t27 = checkout.preview.providerFailure?.sourceBounds;
  let t28;
  if ($[72] !== checkout.error || $[73] !== checkout.method || $[74] !== checkout.preview.error || $[75] !== checkout.preview.providerFailure || $[76] !== checkout.providerFailure || $[77] !== checkout.state || $[78] !== t27) {
    t28 = [checkout.error, checkout.preview.error, checkout.state, checkout.method, t27, checkout.providerFailure, checkout.preview.providerFailure];
    $[72] = checkout.error;
    $[73] = checkout.method;
    $[74] = checkout.preview.error;
    $[75] = checkout.preview.providerFailure;
    $[76] = checkout.providerFailure;
    $[77] = checkout.state;
    $[78] = t27;
    $[79] = t28;
  } else {
    t28 = $[79];
  }
  useEffect(t26, t28);
  let t29;
  if ($[80] !== directPurchase) {
    t29 = directPurchase ? {
      directPurchase
    } : {};
    $[80] = directPurchase;
    $[81] = t29;
  } else {
    t29 = $[81];
  }
  let t30;
  if ($[82] !== realtime) {
    t30 = realtime ? {
      realtime
    } : {};
    $[82] = realtime;
    $[83] = t30;
  } else {
    t30 = $[83];
  }
  let t31;
  if ($[84] !== onAcceptAuthenticationTerms) {
    t31 = onAcceptAuthenticationTerms ? {
      onAcceptAuthenticationTerms
    } : {};
    $[84] = onAcceptAuthenticationTerms;
    $[85] = t31;
  } else {
    t31 = $[85];
  }
  let t32;
  if ($[86] !== onAuthenticateSandbox) {
    t32 = onAuthenticateSandbox ? {
      onAuthenticateSandbox
    } : {};
    $[86] = onAuthenticateSandbox;
    $[87] = t32;
  } else {
    t32 = $[87];
  }
  let t33;
  if ($[88] !== amountDraft || $[89] !== authenticationBusy || $[90] !== authenticationError || $[91] !== authenticationRequested || $[92] !== checkout || $[93] !== client || $[94] !== onClose || $[95] !== onExchange || $[96] !== onSignIn || $[97] !== provider || $[98] !== sessionProofId || $[99] !== t29 || $[100] !== t30 || $[101] !== t31 || $[102] !== t32) {
    t33 = <FundingCheckoutView {...t29} checkout={checkout} amountDraft={amountDraft} client={client} {...t30} sessionProofId={sessionProofId} authenticationRequested={authenticationRequested} authenticationBusy={authenticationBusy} authenticationError={authenticationError} {...t31} {...t32} provider={provider} onSessionRefresh={checkout.retry} onSignIn={onSignIn} onExchange={onExchange} onClose={onClose} currency={checkout.currency} decimals={checkout.decimals} />;
    $[88] = amountDraft;
    $[89] = authenticationBusy;
    $[90] = authenticationError;
    $[91] = authenticationRequested;
    $[92] = checkout;
    $[93] = client;
    $[94] = onClose;
    $[95] = onExchange;
    $[96] = onSignIn;
    $[97] = provider;
    $[98] = sessionProofId;
    $[99] = t29;
    $[100] = t30;
    $[101] = t31;
    $[102] = t32;
    $[103] = t33;
  } else {
    t33 = $[103];
  }
  const content = t33;
  let t34;
  if ($[104] !== content || $[105] !== render) {
    t34 = render ? render(content) : content;
    $[104] = content;
    $[105] = render;
    $[106] = t34;
  } else {
    t34 = $[106];
  }
  return t34;
}
function _temp2(status_0) {
  if (status_0 === "connectionRequired") {
    logger.warn("onramp.reauthentication_required", {
      source: "moonpay_connection_check"
    });
  }
}
function _temp(state) {
  return state.currency;
}